import { gql, useQuery } from '@apollo/client';
import React, { useCallback, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { SelectChangeEvent } from 'typings/events/selection-change-event';

import { useIsAppInBackgroundContext } from 'src/app/appLayout/hooks/useIsAppInBackground';
import { insightsRouteConfig } from 'src/app/graph/routes';
import { CursorProvider } from 'src/components/charts/helpers/Cursor';
import { NoData } from 'src/components/common/noData/NoData';
import { useRouteParams } from 'src/hooks/useRouteParams';
import Config from 'src/lib/config';
import { date } from 'src/lib/date/date';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { mergeQueryParams } from 'src/lib/routing';

import { RequestLatencyDistribution } from './RequestLatencyDistribution';
import { RequestLatencyOverTime } from './RequestLatencyOverTime';
import { RequestsOverTime } from './RequestRateOverTime';
import { useServiceReportHeatmaps } from './ServiceReportHeatmaps';
import { useServiceReportHistograms } from './ServiceReportHistograms';
import { useServiceReportLineCharts } from './ServiceReportLineCharts';
import { SubscriptionEventLatencyDistribution } from './SubscriptionEventLatencyDistribution';
import { SubscriptionEventLatencyOverTime } from './SubscriptionEventLatencyOverTime';
import { SubscriptionNotificationRateOverTime } from './SubscriptionNotificationRateOverTime';
import { OperationMetricType } from './types';

export const serviceReportQuery = gql<
  GraphQLTypes.UI__ServiceReportQuery,
  GraphQLTypes.UI__ServiceReportQueryVariables
>`
  query UI__ServiceReportQuery(
    $graphId: ID!
    $resolution: Resolution!
    $timeFrom: Timestamp!
    $timeTo: Timestamp!
    $filter: ServiceQueryStatsFilter
  ) {
    service(id: $graphId) {
      id
      stats: statsWindow(
        from: $timeFrom
        to: $timeTo
        resolution: $resolution
      ) {
        queryStats(filter: $filter) {
          timestamp
          metrics {
            uncachedHistogram {
              totalCount
              sparseBuckets
              p50: durationMs(percentile: 0.5)
              p90: durationMs(percentile: 0.9)
              p95: durationMs(percentile: 0.95)
            }
            cachedHistogram {
              totalCount
              sparseBuckets
              p50: durationMs(percentile: 0.5)
              p90: durationMs(percentile: 0.9)
              p95: durationMs(percentile: 0.95)
            }
          }
        }
      }
    }
  }
`;

interface Props {
  graphId: string;
  range: Parameters<typeof date.getDateBounds>[0];
  queryId?: string;
  isOperationLandingPage: boolean;
  isSubscription: boolean;
  filter:
    | GraphQLTypes.ServiceErrorStatsFilter
    | GraphQLTypes.ServiceTracePathErrorsRefsFilter
    | GraphQLTypes.ServiceTraceRefsFilter
    | GraphQLTypes.ServiceQueryStatsFilter;
}

export function ServiceReportContent({
  range,
  graphId,
  filter,
  queryId,
  isOperationLandingPage,
  isSubscription,
}: Props) {
  const isInBackground = useIsAppInBackgroundContext();
  const { to, from, fastModeRange } = useRouteParams(insightsRouteConfig);
  const { timeFrom, timeTo, resolution, pollInterval } = date.getDateBounds(
    range,
    { from, to },
    undefined,
    fastModeRange,
  );

  // Requests
  const { data: requestsData, loading: requestsDataLoading } = useQuery(
    serviceReportQuery,
    {
      notifyOnNetworkStatusChange: false,
      pollInterval,
      skip: isInBackground,
      skipPollAttempt: useCallback(() => isInBackground, [isInBackground]),
      variables: {
        graphId,
        resolution,
        timeFrom,
        timeTo,
        filter: {
          queryId,
          ...filter,
          not: {
            operationSubtype: 'subscription-event',
          },
        },
      },
    },
  );

  const loadSubscriptionEvents = isSubscription || !isOperationLandingPage;

  // Subscription Events
  const skip = isInBackground || !loadSubscriptionEvents;
  const { data: eventsData, loading: eventsDataLoading } = useQuery(
    serviceReportQuery,
    {
      notifyOnNetworkStatusChange: false,
      pollInterval,
      skip: isInBackground || !loadSubscriptionEvents,
      skipPollAttempt: useCallback(() => skip, [skip]),
      variables: {
        graphId,
        resolution,
        timeFrom,
        timeTo,
        filter: {
          queryId,
          ...filter,
          operationSubtype: 'subscription-event',
        },
      },
    },
  );

  // Traces
  const location = useLocation();
  const history = useHistory();
  const onTraceBucketSelect = useMemo(() => {
    if (!queryId) return;
    return (index: number) => {
      if (index) {
        history.push({
          ...location,
          search: mergeQueryParams(location.search, {
            [Config.queryParameters.ActiveReportPaneTab]: 'traces',
            [Config.queryParameters.SelectedDurationBucket]: index,
          }),
        });
      }
    };
  }, [history, location, queryId]);

  const requestsChartData: GraphQLTypes.UI__ServiceReportQuery_service_stats_queryStats[] =
    requestsData?.service?.stats?.queryStats || [];

  const eventsChartData: GraphQLTypes.UI__ServiceReportQuery_service_stats_queryStats[] =
    eventsData?.service?.stats?.queryStats || [];

  const isSubscriptionOperation = isOperationLandingPage && isSubscription;

  const [operationMetricType, setOperationMetricType] =
    useState<OperationMetricType>('requestRate');

  const onOperationMetricTypeChange = (
    event: SelectChangeEvent<OperationMetricType>,
  ) => {
    setOperationMetricType(event.target.value);
  };

  const { requestsLineChartStackInfo, eventsLineChartStackInfo } =
    useServiceReportLineCharts(
      requestsChartData,
      eventsChartData,
      resolution,
      isSubscriptionOperation,
      operationMetricType,
    );

  const { requestsHeatmapStackInfo, eventsHeatmapStackInfo } =
    useServiceReportHeatmaps(
      requestsChartData,
      eventsChartData,
      isSubscriptionOperation,
    );

  const {
    requestsHistogramStackInfo,
    eventsHistogramStackInfo,
    requestsHistogramData,
    eventsHistogramData,
  } = useServiceReportHistograms(
    requestsChartData,
    eventsChartData,
    isSubscriptionOperation,
  );

  if (requestsDataLoading || eventsDataLoading || isInBackground) {
    return null;
  }

  if (!requestsData?.service) {
    // Service is a nullable field, so it's totally valid that it can
    // come back null. There's a check in `ServiceLayout` that will
    // short-circuit this tree if the service does not exist. Therefore,
    // throw an error here just incase `ServiceLayout` stops checking
    // and/or so typechecks know that `service` is populated.
    throw new Error('Service is missing');
  }

  // This is the flattest data, a lack of histogram implies a lack of heatmap and stepchart.
  if (
    !requestsHistogramStackInfo.stacks.length &&
    !eventsHistogramStackInfo.stacks.length
  ) {
    return (
      <NoData
        message="No data"
        secondary="Try adjusting time range or resetting filters."
      />
    );
  }

  const showRequestRateOverTime = requestsLineChartStackInfo.stacks.length > 0;
  const showRequestLatencyOverTime = requestsHeatmapStackInfo.stacks.length > 0;
  const showRequestLatencyDistribution =
    requestsHistogramStackInfo.stacks.length > 0;

  const showSubscriptionNotificationRateOverTime =
    eventsLineChartStackInfo.stacks.length > 0;
  const showSubscriptionEventLatencyOverTime =
    eventsHeatmapStackInfo.stacks.length > 0;
  const showSubscriptionEventLatencyDistribution =
    eventsHistogramStackInfo.stacks.length > 0;

  return (
    <CursorProvider>
      {showRequestRateOverTime ? (
        <RequestsOverTime
          isSubscriptionOperation={isSubscriptionOperation}
          requestsLineChartStackInfo={requestsLineChartStackInfo}
          resolution={resolution}
          operationMetricType={operationMetricType}
          onOperationMetricTypeChange={onOperationMetricTypeChange}
        />
      ) : null}

      {showSubscriptionNotificationRateOverTime ? (
        <SubscriptionNotificationRateOverTime
          eventsLineChartStackInfo={eventsLineChartStackInfo}
          resolution={resolution}
        />
      ) : null}

      {showRequestLatencyOverTime ? (
        <RequestLatencyOverTime
          isSubscriptionOperation={isSubscriptionOperation}
          requestsHeatmapStackInfo={requestsHeatmapStackInfo}
          requestsChartData={requestsChartData}
          resolution={resolution}
        />
      ) : null}

      {showSubscriptionEventLatencyOverTime ? (
        <SubscriptionEventLatencyOverTime
          eventsHeatmapStackInfo={eventsHeatmapStackInfo}
          eventsChartData={eventsChartData}
          resolution={resolution}
        />
      ) : null}

      {showRequestLatencyDistribution ? (
        <RequestLatencyDistribution
          isSubscriptionOperation={isSubscriptionOperation}
          requestsHistogramStackInfo={requestsHistogramStackInfo}
          requestsHistogramData={requestsHistogramData}
          onTraceBucketSelect={onTraceBucketSelect}
        />
      ) : null}

      {showSubscriptionEventLatencyDistribution ? (
        <SubscriptionEventLatencyDistribution
          isSubscriptionOperation={isSubscriptionOperation}
          eventsHistogramStackInfo={eventsHistogramStackInfo}
          eventsHistogramData={eventsHistogramData}
          onTraceBucketSelect={onTraceBucketSelect}
        />
      ) : null}
    </CursorProvider>
  );
}
